import { Alert, Button, Grid, TextField } from "@mui/material"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSignupStore } from "../../store/signup/UserSignupStore"
import { SaveAndContinueButton } from "../SaveAndContinueButton/SaveAndContinueButton"
import TabProps from "../TabProps/TabProps"
import { ActionType } from "../../store/signup/SignupReducer"
import { getSignupRequest } from "../../helpers/SignupUtils"
import { saveUserMembershipInfo } from "../../services/ntuc/memberService/membershipSignup"
import { useAlertErrorHandler, useDialogErrorHandler } from "../../hooks/ErrorHandler"
import { ErrorCode, Status } from "../../services/ntuc/applicationError"
import ErrorDialog from "../Dialog/ErrorDialog"
import { SignupRoute } from "../../views/signup-flow/SignupFlow/SignupFlow"
import { trackSignUpStep } from "../../helpers/gtm"
enum DialogButtonAction {
    BACK = `back_to_home`,
    LOGIN = `login`,
    CANCEL = `cancel`,
    DRAFT = `retrieve_draft`,
    CONTINUE = `continue`,
    LOGIN_ANOTHER = `login_another`,
}

const ReferralCode = (props: TabProps) => {
    const { onNavigateTo } = props
    const { t } = useTranslation([`signup`, `common`])

    const [state, dispatch] = useSignupStore()

    const [isLoading, setIsLoading] = useState<boolean>(false)

    const [alert, alertErrorHandler] = useAlertErrorHandler()

    const [dialog, dialogErrorHandler, closeHandler] = useDialogErrorHandler(
        {
            content: ``,
            title: ``,
            display: false,
        },
    )

    const handleDialogClick = (action?: string): void => {
        switch (action) {
            case DialogButtonAction.BACK:
                if (window.myNtucConfig.homeUrl) {
                    window.location.replace(window.myNtucConfig.homeUrl)
                }
                return
            case DialogButtonAction.LOGIN_ANOTHER:
                if (window.myNtucConfig.loginUrl) {
                    window.location.replace(window.myNtucConfig.loginUrl)
                }
                return
            case DialogButtonAction.CANCEL:
                return closeHandler()
            default:
                break
        }
    }

    useEffect(() => {
        dispatch({
            type: ActionType.ADD_REFERRAL_DETAILS,
            value: {
                ...state.referralDetail,
                referralCode: state.referralDetail.referralCode ? state.referralDetail.referralCode : window.myNtucConfig.referralCode,
                recruiterCode: state.referralDetail.recruiterCode ? state.referralDetail.recruiterCode : window.myNtucConfig.recruiterCode,
            },
        })
    }, [])

    const handleReferralCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: ActionType.ADD_REFERRAL_DETAILS,
            value: {
                ...state.referralDetail,
                referralCode: event.target.value,
            },
        })
    }

    const handleRecruiterCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({
            type: ActionType.ADD_REFERRAL_DETAILS,
            value: {
                ...state.referralDetail,
                recruiterCode: event.target.value,
            },
        })
    }

    const handleSaveAndContinueClick = () => {
        trackSignUpStep(`ReferralCode`)
        setIsLoading(true)
        saveForm()
    }

    const saveForm = () => {
        const request = getSignupRequest(state)

        saveUserMembershipInfo(request, state.membershipId)
            .then(response => {
                if (response?.metadata.status === Status.SUCCESS) {
                    onNavigateTo(SignupRoute.Review)
                } else {
                    alertErrorHandler()
                }
            })
            .catch(error => {
                if (error.response) {
                    alertErrorHandler(error.response.data.content)
                    dialogErrorHandler(error.response.data.content)
                } else if (error.errorCode in ErrorCode) {
                    alertErrorHandler(error)
                    dialogErrorHandler(error)
                }
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const tabColumnSpacing = {
        xs: `16px`,
        sm: `24px`,
        md: `24px`,
        lg: `40px`,
    }

    return (
        < Grid container rowSpacing={4} columnSpacing={tabColumnSpacing}>
            <ErrorDialog open={dialog.display} content={t(dialog.content, { ns: `common` })} title={t(dialog.title, { ns: `common` })} onClose={closeHandler}>
                {dialog.primaryButton && <Button onClick={() => handleDialogClick(dialog.primaryButton)} fullWidth variant="contained">{t(dialog.primaryButton, { ns: `common` })}</Button>}
                {dialog.secondaryButton && <Button onClick={() => handleDialogClick(dialog.secondaryButton)} fullWidth>{t(dialog.secondaryButton, { ns: `common` })}</Button>}
            </ErrorDialog>
            <Grid item xs={12}>
                {alert && alert.display && <Alert severity="error">{t(alert.content)}</Alert>}
            </Grid>
            <Grid container item justifyContent="center" xs={12}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        id="referral-code"
                        label={t(`referral.referral_code_prompt`)}
                        value={state.referralDetail.referralCode}
                        onChange={handleReferralCodeChange}
                        type="number"
                        inputProps={{ inputMode: `numeric`, pattern: `[0-9]*` }}
                    />
                </Grid>
            </Grid>

            <Grid container justifyContent="center" item xs={12}>
                <Grid item xs={6}>
                    <TextField
                        fullWidth
                        id="recruiter-code"
                        label={t(`referral.recruiter_code_placeholder`)}
                        value={state.referralDetail.recruiterCode}
                        onChange={handleRecruiterCodeChange}
                    />
                </Grid>
            </Grid>
            <Grid container item xs={12} justifyContent="center">
                <SaveAndContinueButton loading={isLoading} onClick={handleSaveAndContinueClick} />
            </Grid>
        </Grid >
    )
}

export default ReferralCode